import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FloatingMenuTab from '../../Components/ui/FloatingMenuTab';
import { X } from 'lucide-react';
import { useDashboardTour } from '../../hooks/useDashboardTour';

// Icon imports
import IconDash from '../../assets/Icons/IconDash.png';
import IconPublProfile from '../../assets/Icons/IconPublprofile.png';
import IconUpcomEvent from '../../assets/Icons/IconUpcomEvent.png';
import IconMessages from '../../assets/Icons/IconMessages.png';
import IconAnalytics from '../../assets/Icons/IconAnalytics.png';
import IconFinances from '../../assets/Icons/IconFinances.png';
import IconNews from '../../assets/Icons/IconNews.png';
import IconSettings from '../../assets/Icons/IconSettings.png';
import IconCalendar from '../../assets/Icons/IconCalendar.png';
import IconBookingRequests from '../../assets/Icons/IconBookingRequests.png';
import TourIcon from '../../assets/Icons/TourIcon.png';
import IconCreateEvent from '../../assets/Icons/IconCreateEvent2.png';
import IconBrowseTalent from '../../assets/Icons/IconBrowseTalent2.png';
import IconContract from '../../assets/Icons/IconContract.png';
import IconRating from '../../assets/Icons/IconRating.png';
import IconVerification from '../../assets/Icons/IconVerified2.png';
import IconNotifications from '../../assets/Icons/IconUrgent.png';
import GigManagementIcon from '../../assets/Icons/GigManagementIcon.png';
import AnimatedBellIcon from '../../Components/Notifications/AnimatedBellIcon';
import debug from '../../utils/debug';

// Simple hint management for mobile menu (no auto-hide)
const useMenuHint = () => {
  const [hasShownHint, setHasShownHint] = useState(() => {
    return localStorage.getItem('menuHintShown') === 'true';
  });
  
  // Show hint for first-time users
  useEffect(() => {
    if (!hasShownHint) {
      const timer = setTimeout(() => {
        setHasShownHint(true);
        localStorage.setItem('menuHintShown', 'true');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [hasShownHint]);
  
  return { showHint: !hasShownHint };
};

const SidebarItem = ({ icon, text, onClick, isActive, isComingSoon, scaleFactor = 1, dataTour }) => (
  <li
    onClick={onClick}
    data-tour={dataTour}
    style={{
      display: 'flex',
      alignItems: 'left',
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 'bold',
      fontSize: `${14 * scaleFactor}px`,
      marginBottom: `${30 * scaleFactor}px`,
      cursor: isComingSoon && process.env.NODE_ENV !== 'development' ? 'not-allowed' : 'pointer',
      color: isComingSoon ? '#888' : (isActive ? '#f85008' : 'white'),
      opacity: isComingSoon ? 0.6 : 1,
      transition: 'transform 0.2s ease'
    }}
    onMouseEnter={(e) => {
      if (!isComingSoon) {
        e.currentTarget.style.transform = `translateX(${10 * scaleFactor}px)`;
      }
    }}
    onMouseLeave={(e) => {
      if (!isComingSoon) {
        e.currentTarget.style.transform = 'translateX(0)';
      }
    }}
  >
    <img src={icon} alt={`${text} Icon`} style={{ width: `${22 * scaleFactor}px`, height: `${22 * scaleFactor}px`, marginRight: `${15 * scaleFactor}px`, filter: isComingSoon ? 'grayscale(100%)' : 'none' }} />
    {text}
    {isComingSoon && (
      <span style={{
        marginLeft: 'auto',
        fontSize: `${11 * scaleFactor}px`,
        fontWeight: 'normal',
        fontStyle: 'italic',
        color: '#aaa',
        backgroundColor: 'rgba(255,255,255,0.1)',
        padding: `${2 * scaleFactor}px ${8 * scaleFactor}px`,
        borderRadius: `${10 * scaleFactor}px`
      }}>
        Coming Soon
      </span>
    )}
  </li>
);

const QuickActionItem = ({ icon, text, onClick, isActive, isComingSoon, scaleFactor = 1 }) => (
  <li
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 'bold',
      fontSize: `${13 * scaleFactor}px`,
      marginBottom: `${16 * scaleFactor}px`,
      cursor: isComingSoon && process.env.NODE_ENV !== 'development' ? 'not-allowed' : 'pointer',
      color: isComingSoon ? '#888' : (isActive ? '#f85008' : '#01a2f1'),
      backgroundColor: isComingSoon ? 'rgba(200,200,200,0.3)' : 'rgba(255,255,255,0.9)',
      padding: `${10 * scaleFactor}px ${12 * scaleFactor}px`,
      borderRadius: `${20 * scaleFactor}px`,
      transition: 'all 0.3s ease',
      opacity: isComingSoon ? 0.7 : 1,
      position: 'relative'
    }}
    onMouseEnter={(e) => {
      if (!isComingSoon) {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = '#01a2f1';
          e.currentTarget.style.color = 'white';
        }
        e.currentTarget.style.transform = 'translateY(-2px)';
      }
    }}
    onMouseLeave={(e) => {
      if (!isComingSoon) {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.9)';
          e.currentTarget.style.color = '#01a2f1';
        } else {
          e.currentTarget.style.color = '#f85008';
        }
        e.currentTarget.style.transform = 'translateY(0)';
      }
    }}
  >
    <img src={icon} alt={`${text} Icon`} style={{ width: `${20 * scaleFactor}px`, height: `${20 * scaleFactor}px`, marginRight: `${12 * scaleFactor}px`, filter: isComingSoon ? 'grayscale(100%)' : 'none' }} />
    {text}
    {isComingSoon && (
      <span style={{
        marginLeft: 'auto',
        fontSize: `${10 * scaleFactor}px`,
        fontWeight: 'normal',
        fontStyle: 'italic',
        color: '#666',
        backgroundColor: 'rgba(0,0,0,0.1)',
        padding: `${2 * scaleFactor}px ${6 * scaleFactor}px`,
        borderRadius: `${8 * scaleFactor}px`
      }}>
        Coming Soon
      </span>
    )}
  </li>
);

const OrganiserSidebar = ({ currentUser, isMobileOpen = false, onMobileClose, scaleFactor: propScaleFactor }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const [internalScaleFactor, setInternalScaleFactor] = useState(1);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Tour management
  const { resetTourState } = useDashboardTour();
  
  // Use prop scaleFactor if provided, otherwise calculate internally
  const scaleFactor = propScaleFactor !== undefined ? propScaleFactor : internalScaleFactor;
  
  // Calculate scale factor based on viewport width (1200px-1900px) only if not provided as prop
  useEffect(() => {
    if (propScaleFactor === undefined) {
      const calculateScale = () => {
        const width = window.innerWidth;
        if (width >= 1900) {
          setInternalScaleFactor(1);
        } else if (width <= 1200) {
          setInternalScaleFactor(0.75); // Minimum scale at 1200px
        } else {
          // Linear interpolation between 0.75 and 1 for widths 1200-1900
          const scale = 0.75 + ((width - 1200) / 700) * 0.25;
          setInternalScaleFactor(scale);
        }
      };
      
      calculateScale();
      window.addEventListener('resize', calculateScale);
      return () => window.removeEventListener('resize', calculateScale);
    }
  }, [propScaleFactor]);
  
  // Add hint management hook
  const { showHint } = useMenuHint();
  
    const handleNavigation = (page, path, isComingSoon) => {
    // Allow navigation in development mode or if not coming soon
    if (process.env.NODE_ENV === 'development' || !isComingSoon) {
      navigate(path);
    }
    // Close mobile menu after navigation
    setIsMobileMenuOpen(false);
  };

  // Tour trigger handler
  const handleStartTour = () => {
    // Only allow tour functionality in development mode
    if (process.env.NODE_ENV !== 'development') {
      return; // Do nothing in production
    }

    resetTourState();
    // Close mobile menu if open
    if (isMobileOpen && onMobileClose) onMobileClose();
    setIsMobileMenuOpen(false);
  };

  // Define the mapping of sidebar items to their paths
  const sidebarItems = [
    { text: "Dashboard", path: `/organiser-dashboard/${currentUser?.uid}`, icon: IconDash, dataTour: "organiser-sidebar-dashboard" },
    { text: "Notifications", path: `/organiser-notifications/${currentUser?.uid}`, icon: IconNotifications, dataTour: "organiser-sidebar-notifications" },
    { text: "Inbox", path: `/organiser-inbox/${currentUser?.uid}`, icon: IconMessages, dataTour: "organiser-sidebar-inbox" },
    { text: "Gig Calendar", path: `/organiser-calendar/${currentUser?.uid}`, icon: IconCalendar, dataTour: "organiser-sidebar-gig-calendar" },
    { text: "Gig Management", path: `/organiser-event-management/${currentUser?.uid}`, icon: GigManagementIcon, dataTour: "organiser-sidebar-gig-management" },
    { text: "Bookings Management", path: `/booking-management/${currentUser?.uid}`, icon: IconUpcomEvent, dataTour: "organiser-sidebar-bookings-management" },
    { text: "Contracts Locker", path: `/organiser-contracts-locker/${currentUser?.uid}`, icon: IconContract, dataTour: "organiser-sidebar-contracts-locker" },
    { text: "Analytics", path: `/organiser-analytics/${currentUser?.uid}`, icon: IconAnalytics, dataTour: "organiser-sidebar-analytics" },
    { text: "Ratings & Reviews", path: `/organiser-rating/${currentUser?.uid}`, icon: IconRating, dataTour: "organiser-sidebar-ratings-reviews" },
    { text: "Settings", path: `/organiser-settings/${currentUser?.uid}`, icon: IconSettings, dataTour: "organiser-sidebar-settings" },
    { text: "Dashboard Tour", path: null, icon: TourIcon, isTour: true, isComingSoon: process.env.NODE_ENV === 'production' },
    { text: "News", path: `/organiser-news/${currentUser?.uid}`, icon: IconNews, isComingSoon: true },
    { text: "Profile Verification", path: `/organiser-verification/${currentUser?.uid}`, icon: IconVerification, isComingSoon: true },
  ];

  const quickActionItems = [
    { text: "Browse Talent", path: `/browse-talent/${currentUser?.uid}`, icon: IconBrowseTalent },
    { text: "Create New Gig", path: `/organiser-event/${currentUser?.uid}`, icon: IconCreateEvent }
  ];

  // Check if a path is the current active path
  const isActive = (path) => {
    return currentPath === path;
  };

  // If mobile overlay mode, render as prominent overlay
  if (isMobileOpen) {
    return (
      <>
        {/* Mobile Overlay Background */}
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={onMobileClose}
        />
        
        {/* Mobile Sidebar */}
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            width: '300px',
            backgroundColor: 'linear-gradient(135deg, #144874 0%, #0d3659 100%)',
            background: 'linear-gradient(135deg, #144874 0%, #0d3659 100%)',
            color: 'white',
            zIndex: 10001,
            padding: '20px',
            overflowY: 'auto',
            boxShadow: '4px 0 20px rgba(0, 0, 0, 0.3)',
            transform: 'translateX(0)',
            transition: 'transform 0.3s ease-in-out'
          }}
        >
          {/* Close Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px',
            paddingBottom: '15px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#f85008'
            }}>Menu</h2>
            <button
              onClick={onMobileClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <X size={24} />
            </button>
          </div>
          
          {/* Menu Items */}
          <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
            {sidebarItems.map((item, index) => (
              <li
                key={index}
                onClick={() => {
                  handleNavigation(item.text, item.path, item.isComingSoon);
                  onMobileClose();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  marginBottom: '25px',
                  cursor: item.isComingSoon && process.env.NODE_ENV !== 'development' ? 'not-allowed' : 'pointer',
                  color: item.isComingSoon ? '#888' : (isActive(item.path) ? '#f85008' : 'white'),
                  opacity: item.isComingSoon ? 0.6 : 1,
                  padding: '12px 16px',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  backgroundColor: isActive(item.path) ? 'rgba(248, 80, 8, 0.1)' : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (!item.isComingSoon) {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateX(5px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!item.isComingSoon) {
                    e.currentTarget.style.backgroundColor = isActive(item.path) ? 'rgba(248, 80, 8, 0.1)' : 'transparent';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }
                }}
              >
                <img 
                  src={item.icon} 
                  alt={`${item.text} Icon`} 
                  style={{ 
                    width: '24px', 
                    height: '24px', 
                    marginRight: '15px', 
                    filter: item.isComingSoon ? 'grayscale(100%)' : 'none' 
                  }} 
                />
                {item.text}
                {item.isComingSoon && (
                  <span style={{
                    marginLeft: '10px',
                    fontSize: '10px',
                    backgroundColor: '#f85008',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '10px'
                  }}>
                    SOON
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="sidebar desktop-sidebar" data-tour="organiser-sidebar" style={{
        backgroundColor: 'transparent',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'left',
        padding: `${20 * scaleFactor}px`,
        paddingTop: `${20 * scaleFactor}px`, // Reduced top padding to move nav items closer to top
        width: `${284 * scaleFactor}px`, // Reduced by 16px (1rem)
        border: 'none',
        boxShadow: 'none',
        borderRight: 'none',
        borderImage: 'none',
        transition: 'all 0.3s ease'
      }}>
      <ul style={{ listStyleType: 'none', padding: 0, margin: 0, width: '100%' }}>
        {sidebarItems.map((item, index) => (
          <SidebarItem
            key={index}
            icon={item.icon}
            text={item.text}
            onClick={() => item.isTour ? handleStartTour() : handleNavigation(item.text, item.path, item.isComingSoon)}
            isActive={!item.isTour && isActive(item.path)}
            isComingSoon={item.isComingSoon}
            scaleFactor={scaleFactor}
            dataTour={item.dataTour}
          />
        ))}
      </ul>
      
      {/* Quick Actions Section */}
      <div data-tour="quick-actions" style={{ width: '100%', marginTop: `${30 * scaleFactor}px` }}>
        <h3 style={{ 
          color: 'white', 
          fontFamily: 'Montserrat, sans-serif', 
          fontSize: `${16 * scaleFactor}px`,
          marginBottom: `${16 * scaleFactor}px`,
          borderBottom: '1px solid rgba(255,255,255,0.3)',
          paddingBottom: `${8 * scaleFactor}px`
        }}>
          Quick Actions
        </h3>
        <ul style={{ listStyleType: 'none', padding: 0, margin: 0, width: '100%' }}>
          {quickActionItems.map((item, index) => (
            <QuickActionItem 
              key={index}
              icon={item.icon} 
              text={item.text} 
              onClick={() => handleNavigation(item.text, item.path, item.isComingSoon)}
              isActive={isActive(item.path)}
              isComingSoon={item.isComingSoon}
              scaleFactor={scaleFactor}
            />
          ))}
        </ul>
      </div>
      </div>

      {/* Mobile Floating Menu Tab - Only visible on mobile */}
      <div className="mobile-menu-container">
        <FloatingMenuTab
          isOpen={isMobileMenuOpen}
          onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          initialPosition={{ y: window.innerHeight / 2 - 40 }}
        >
          <nav className="mobile-nav-content">
            {/* Dashboard */}
            <button
              className={`menu-item ${isActive(`/organiser-dashboard/${currentUser?.uid}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Dashboard', `/organiser-dashboard/${currentUser?.uid}`)}
            >
              <img src={IconDash} alt="" className="menu-icon" />
              <span>Dashboard</span>
            </button>

            {/* Notifications */}
            <button
              className={`menu-item ${isActive(`/organiser-notifications/${currentUser?.uid}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Notifications', `/organiser-notifications/${currentUser?.uid}`)}
            >
              <AnimatedBellIcon
                userId={currentUser?.uid}
                userType="organiser"
                iconSrc={IconNotifications}
                className="menu-icon"
              />
              <span>Notifications</span>
            </button>

            {/* Inbox */}
            <button
              className={`menu-item ${isActive(`/organiser-inbox/${currentUser?.uid}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Inbox', `/organiser-inbox/${currentUser?.uid}`)}
            >
              <img src={IconMessages} alt="" className="menu-icon" />
              <span>Inbox</span>
            </button>

            {/* Gig Calendar */}
            <button
              className={`menu-item ${isActive(`/organiser-calendar/${currentUser?.uid}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Gig Calendar', `/organiser-calendar/${currentUser?.uid}`)}
            >
              <img src={IconCalendar} alt="" className="menu-icon" />
              <span>Gig Calendar</span>
            </button>

            {/* Gig Management */}
            <button
              className={`menu-item ${isActive(`/organiser-event-management/${currentUser?.uid}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Gig Management', `/organiser-event-management/${currentUser?.uid}`)}
            >
              <img src={GigManagementIcon} alt="" className="menu-icon" />
              <span>Gig Management</span>
            </button>

            {/* Bookings Management */}
            <button
              className={`menu-item ${isActive(`/booking-management/${currentUser?.uid}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Bookings Management', `/booking-management/${currentUser?.uid}`)}
            >
              <img src={IconUpcomEvent} alt="" className="menu-icon" />
              <span>Bookings Management</span>
            </button>

            {/* Contracts Locker */}
            <button
              className={`menu-item ${isActive(`/organiser-contracts-locker/${currentUser?.uid}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Contracts Locker', `/organiser-contracts-locker/${currentUser?.uid}`)}
            >
              <img src={IconContract} alt="" className="menu-icon" />
              <span>Contracts Locker</span>
            </button>

            {/* Analytics */}
            <button
              className={`menu-item ${isActive(`/organiser-analytics/${currentUser?.uid}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Analytics', `/organiser-analytics/${currentUser?.uid}`)}
            >
              <img src={IconAnalytics} alt="" className="menu-icon" />
              <span>Analytics</span>
            </button>

            {/* Ratings & Reviews */}
            <button
              className={`menu-item ${isActive(`/organiser-rating/${currentUser?.uid}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Ratings & Reviews', `/organiser-rating/${currentUser?.uid}`)}
            >
              <img src={IconRating} alt="" className="menu-icon" />
              <span>Ratings & Reviews</span>
            </button>

            {/* Settings */}
            <button
              className={`menu-item ${isActive(`/organiser-settings/${currentUser?.uid}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Settings', `/organiser-settings/${currentUser?.uid}`)}
            >
              <img src={IconSettings} alt="" className="menu-icon" />
              <span>Settings</span>
            </button>

            {/* News (Coming Soon) */}
            <button
              className="menu-item coming-soon"
              onClick={() => handleNavigation('News', `/organiser-news/${currentUser?.uid}`, true)}
              disabled
            >
              <img src={IconNews} alt="" className="menu-icon" style={{ filter: 'grayscale(100%)' }} />
              <span>News</span>
              <span className="badge">Coming Soon</span>
            </button>

            {/* Dashboard Tour */}
            <button
              className={`menu-item ${process.env.NODE_ENV === 'production' ? 'coming-soon' : ''}`}
              onClick={handleStartTour}
              disabled={process.env.NODE_ENV === 'production'}
            >
              <img src={TourIcon} alt="" className="menu-icon" style={{ filter: process.env.NODE_ENV === 'production' ? 'grayscale(100%)' : 'none' }} />
              <span>Dashboard Tour</span>
              {process.env.NODE_ENV === 'production' && <span className="badge">Coming Soon</span>}
            </button>

            {/* Profile Verification (Coming Soon) */}
            <button
              className="menu-item coming-soon"
              onClick={() => handleNavigation('Profile Verification', `/organiser-verification/${currentUser?.uid}`, true)}
              disabled
            >
              <img src={IconVerification} alt="" className="menu-icon" style={{ filter: 'grayscale(100%)' }} />
              <span>Profile Verification</span>
              <span className="badge">Coming Soon</span>
            </button>

            {/* Quick Actions Divider */}
            <div className="menu-divider">
              <span>Quick Actions</span>
            </div>

            {/* Browse Talent */}
            <button
              className={`menu-item quick-action ${isActive(`/browse-talent/${currentUser?.uid}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Browse Talent', `/browse-talent/${currentUser?.uid}`)}
            >
              <img src={IconBrowseTalent} alt="" className="menu-icon" />
              <span>Browse Talent</span>
            </button>

            {/* Create New Gig */}
            <button
              className={`menu-item quick-action ${isActive(`/organiser-event/${currentUser?.uid}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Create New Gig', `/organiser-event/${currentUser?.uid}`)}
            >
              <img src={IconCreateEvent} alt="" className="menu-icon" />
              <span>Create New Gig</span>
            </button>
          </nav>
        </FloatingMenuTab>
      </div>

      <style>{`
        /* Desktop styles */
        @media (min-width: 1201px) {
          .mobile-menu-container {
            display: none !important;
          }
        }

        /* Mobile and tablet styles */
        @media (max-width: 1200px) {
          .desktop-sidebar {
            display: none !important;
          }
        }

        /* Mobile Navigation Content Styles - matching TalentSidebar */
        .mobile-nav-content {
          display: flex;
          flex-direction: column;
          gap: 3px;  /* Matching TalentSidebar */
        }

        .mobile-nav-content .menu-item {
          display: flex;
          align-items: center;
          gap: 11px;  /* Matching TalentSidebar */
          padding: 11px 15px;  /* Matching TalentSidebar */
          background: none;
          border: none;
          color: white;
          font-family: 'Montserrat', sans-serif;
          font-size: 14px;  /* Matching TalentSidebar */
          font-weight: 600;
          text-align: left;
          cursor: pointer;
          transition: all 0.2s ease;
          border-radius: 8px;  /* Matching TalentSidebar */
          width: 100%;
          position: relative;
        }

        .mobile-nav-content .menu-item:hover {
          background: linear-gradient(135deg, rgba(248, 80, 8, 0.1) 0%, rgba(255, 107, 43, 0.05) 100%);
          transform: translateX(4px);
        }

        .mobile-nav-content .menu-item.active {
          color: #f85008;
        }

        .mobile-nav-content .menu-item.coming-soon {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .mobile-nav-content .menu-icon {
          width: 20px;
          height: 20px;
          object-fit: contain;
          flex-shrink: 0;
        }

        .mobile-nav-content .badge {
          margin-left: auto;
          font-size: 10px;
          padding: 2px 6px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
          color: rgba(255, 255, 255, 0.7);
        }

        .mobile-nav-content .menu-divider {
          margin: 16px 0 8px;
          padding: 8px 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          color: rgba(255, 255, 255, 0.7);
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .mobile-nav-content .quick-action {
          background: rgba(248, 80, 8, 0.05);
        }

        .mobile-nav-content .quick-action:hover {
          background: linear-gradient(135deg, rgba(248, 80, 8, 0.15) 0%, rgba(255, 107, 43, 0.1) 100%);
        }
      `}</style>
    </>
  );
};

export default OrganiserSidebar;
